import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect, useDispatch } from 'react-redux';

import Box from '@mui/material/Box';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';

import CaseModal from '../modals/case';
import Button from '../shared/Button';
import Alert from '../shared/Alert';
import Input from '../shared/Input';
import CasesTable from '../cases/CasesTable';
import LoadScreen from '../../components/load-screen';

import {
  setCases,
  setActiveCase,
  discardActiveCase,
  fetchCaseById,
  fetchCasesByPatientData,
  fetchRejectedCases,
} from '../../actions/casesActions';
import { fetchHospitals } from '../../actions/adminActions';
import { fetchSurgeons } from '../../actions/surgeonsActions';
import { fetchProcedures } from '../../actions/proceduresActions';

import './dashboard.scss';

const RadiologyCaseContainer = (props) => {
  const {
    cases,
    setCases,
    setActiveCase,
    discardActiveCase,
    fetchCaseById,
    fetchCasesByPatientData,
    fetchRejectedCases,
    procedures,
    userId,
    distributors,
    companies,
  } = props;

  const dispatch = useDispatch();

  const [isOpen, toggleModal] = useState(false);
  const [caseId, setCaseId] = useState('');
  const [date, setDate] = useState(null);
  const [lastName, setLastName] = useState('');

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  useEffect(() => {
    dispatch(fetchHospitals());
    dispatch(fetchProcedures());
    dispatch(fetchSurgeons());

    fetchCases().catch(console.error);
  }, [dispatch, userId]);

  const fetchCases = async () => {
    try {
      setFetching(true);
      const cases = await fetchRejectedCases();
      setCases(
        cases?.filter((item) => item.step === 1).map((item) => {
          const procedureName = procedures.find((p) => p.id === item?.procedure)?.name;
          return { ...item, procedureName, access: true };
        }) || []
      );
    } catch (err) {
      throw err;
    } finally {
      setFetching(false);
    }
  };

  const handleModalClose = () => {
    toggleModal(false);
    discardActiveCase();
  };

  const onSearchClick = async () => {
    setLoading(true);

    try {
      if (caseId) {
        const doc = await fetchCaseById(caseId);

        if (doc && (doc.step === 1 || doc?.radiologyUser === userId)) {
          const procedureName = procedures.find((p) => p.id === doc?.procedure)?.name;
          setCases([{ ...doc, access: doc?.step === 1, procedureName: doc?.procedureName || procedureName }]);
          setCaseId('');
          setDate(null);
          setLastName('');
        } else {
          setError("No case found, please modify inputs and retry (last name is case-sensitive)");
        }
      } else if (lastName && date) {
        const docs = await fetchCasesByPatientData(lastName, date.startOf('day'));
        if (docs && docs.filter((doc) => doc && (doc.step === 1 || doc?.radiologyUser === userId))) {
          setCases(docs?.map((item) => {
            const procedureName = procedures.find((p) => p.id === item?.procedure)?.name;
            return { ...item, access: item?.step === 1, procedureName: item.procedureName || procedureName };
          }));
          setCaseId('');
          setDate(null);
          setLastName('');
        } else {
          setError("No case found, please modify inputs and retry (last name is case-sensitive)");
        }
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleClick = (caseItem) => {
    setActiveCase(caseItem);
    toggleModal(true);
  };

  const isDisabled = () => {
    return !caseId && (!date || !lastName);
  };

  return (
    <Box className="cases-table-container">
      {fetching && <LoadScreen />}
      <div className="table-title-container">
        <div className="flex-1">
          <div className="d-flex">
            <Box>
              <Box className="table-title">
                Search Cases
              </Box>
            </Box>
          </div>
        </div>
      </div>

      <Box sx={{ ml: 3, mb: 3 }} className="secondary">
        Please provide Unique References if available or search via patient DOB & last name
      </Box>

      <Box className='d-flex space-between p-l-md p-r-md m-t-lg m-b-lg align-start'>
        <div className="flex-1 m-l-lg" style={{ maxWidth: 300 }}>
          <div className="secondary uppercase m-b-sm">Unique reference</div>
          <Input
            id="caseId"
            type="text"
            fullWidth
            value={caseId}
            onChange={(e) => setCaseId(e.target.value)}
          />
        </div>
        <div className="m-l-lg flex-1" style={{ marginLeft: 48, maxWidth: 300 }}>
          <div className="secondary uppercase m-b-sm">Patient date of birth</div>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DatePicker
              format="DD/MM/YYYY"
              value={date}
              onChange={(newDate) => setDate(newDate)}
              disableFuture
              slotProps={{
                textField: {
                  fullWidth: true,
                  variant: 'standard',
                },
              }}
            />
          </LocalizationProvider>
        </div >
        <div className="m-l-lg flex-1" style={{ maxWidth: 300 }}>
          <div className="secondary uppercase m-b-sm">Patient last name (case-sensitive)</div>
          <Input
            id="lastName"
            type="text"
            fullWidth
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
          />
        </div>

        <div style={{ marginTop: 24, marginLeft: 16, marginRight: 16 }}>
          <Button
            text="Search"
            type="submit"
            color="primary"
            onClick={onSearchClick}
            disabled={loading || isDisabled()}
          />
        </div>
      </Box>

      <div style={{ marginTop: 36, paddingBottom: 8 }}>
        <CasesTable
          cases={cases}
          onClick={handleClick}
          radiology
          distributors={distributors}
          companies={companies}
          page={page}
          setPage={setPage}
          rowsPerPage={rowsPerPage}
          setRowsPerPage={setRowsPerPage}
        />
      </div>

      <CaseModal open={isOpen} onClose={handleModalClose} />

      <Alert
        variant="warning"
        message={error}
        open={!!error}
        onClose={() => setError(null)}
      />
    </Box>
  );
};

RadiologyCaseContainer.propTypes = {
  setActiveCase: PropTypes.func,
  discardActiveCase: PropTypes.func,
};

const mapStateToProps = (state) => {
  const procedures = state.procedures.list;
  const users = state.users.list;
  const cases = state.cases.list.map((item) => {
    const caseProcedure = procedures.find((procedure) => procedure.id === item.procedure);

    return {
      ...item,
      procedureName: caseProcedure ? caseProcedure.name : 'No procedure',
    };
  });
  const trainingMode = state.sidebar.trainingMode;
  const userId = state.user.currentUser.uid;

  return {
    cases: cases.filter((item) => (item.step === 1 || item?.radiologyUser === userId)),
    trainingMode,
    users,
    procedures: state.procedures.list,
    userId,
    distributors: state.distributors.list,
    companies: state.companies.list,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    setCases: (cases) => dispatch(setCases(cases)),
    setActiveCase: (newCase) => dispatch(setActiveCase(newCase)),
    discardActiveCase: () => dispatch(discardActiveCase()),
    fetchCaseById: (caseId) => dispatch(fetchCaseById(caseId)),
    fetchCasesByPatientData: (lastName, date) => dispatch(fetchCasesByPatientData(lastName, date)),
    fetchProcedures: () => dispatch(fetchProcedures()),
    fetchRejectedCases: () => dispatch(fetchRejectedCases()),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(RadiologyCaseContainer);
